import { getResultWithError } from "../services.utils";
import { AuthService, AuthStatusUser } from "./auth.service";

export class AuthServiceImpl implements AuthService {
    appAPIUrl: string;
    showSnackbar: (message: string, severity: string) => void;

    constructor(appAPIUrl: string, showSnackbar: (message: string, severity: string) => void) {
        this.appAPIUrl = appAPIUrl;
        this.showSnackbar = showSnackbar;
    }

    localLogin = async (site: string, email: string, password: string): Promise<void> => {
        const response = await fetch(`${this.appAPIUrl}/auth/login/${site}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ email, password }),
            credentials: "include", // Important to include cookies in the request
        });
        return getResultWithError(response, this.showSnackbar);
    }

    providerLogin = (site: string, provider: string): void => {
        window.location.href = `${this.appAPIUrl}/auth/login/${site}/${provider}`;
    }

    forgotPassword = async (email: string, site: string): Promise<void> => {
        const response = await fetch(`${this.appAPIUrl}/auth/forgot-password`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ email, site }),
        });
        if (response.status === 429) {
            let message = "Too many requests, please try again later";
            try {
                const json = await response.json();
                if (typeof json?.message === "string" && json.message.length > 0) {
                    message = json.message;
                }
            } catch {
                // Keep the fallback sentence when the limiter body is not JSON.
            }
            const error = new Error(message) as Error & { status: number };
            error.status = 429;
            throw error;
        }
        return getResultWithError(response, this.showSnackbar);
    }

    changePassword = async (currentPassword: string | undefined, newPassword: string): Promise<void> => {
        const response = await fetch(`${this.appAPIUrl}/auth/change-password`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ currentPassword: currentPassword || undefined, newPassword }),
            credentials: "include", // Important to include cookies for authentication
        });
        return getResultWithError(response, this.showSnackbar);
    }

    setPassword = async (token: string, newPassword: string): Promise<void> => {
        const response = await fetch(`${this.appAPIUrl}/auth/set-password`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ token, newPassword }),
        });
        return getResultWithError(response, this.showSnackbar);
    }

    checkAuthStatus = async (site: string): Promise<{ isAuthenticated: boolean; user?: AuthStatusUser }> => {
        try {
            const response = await fetch(`${this.appAPIUrl}/auth/status/${site}`, {
                method: "GET",
                credentials: "include", // Important to include cookies in the request
            });

            if (response.ok) {
                const data = await response.json() as { user?: Partial<AuthStatusUser> };
                const user = data.user;
                if (!user) {
                    return { isAuthenticated: true };
                }
                return {
                    isAuthenticated: true,
                    user: {
                        id: user.id ?? "",
                        name: user.name ?? "",
                        email: user.email ?? "",
                        provider: user.provider ?? "",
                    },
                };
            } else {
                return { isAuthenticated: false };
            }
        } catch (error) {
            console.error("Error checking auth status:", error);
            return { isAuthenticated: false };
        }
    }

    logout = async (site: string): Promise<void> => {
        try {
            const response = await fetch(`${this.appAPIUrl}/auth/logout/${site}`, {
                method: "POST",
                credentials: "include", // Important to include cookies in the request
            });
            
            if (!response.ok) {
                throw new Error("Logout failed");
            }
        } catch (error) {
            console.error("Error during logout:", error);
            throw error;
        }
    }
}